import type { CriteriaWeights, MatchingResult, MatchingResultWithScore } from '../types/matching.types';

/**
 * Poids par défaut des 4 critères ajustables
 */
export const DEFAULT_WEIGHTS: CriteriaWeights = {
  poste: 3,
  competences: 3,
  experience: 3,
  secteurs: 3,
};

/**
 * Labels des critères ajustables
 */
export const CRITERIA_LABELS: Record<keyof CriteriaWeights, string> = {
  poste: 'Poste',
  competences: 'Compétences',
  experience: 'Expérience',
  secteurs: 'Secteurs',
};

/**
 * Labels de tous les critères (pour affichage des barres de score)
 */
export const ALL_CRITERIA_LABELS: Record<string, string> = {
  poste: 'Poste',
  competences: 'Compétences',
  experience: 'Expérience',
  secteurs: 'Secteurs',
  localisation: 'Localisation',
  budget: 'Budget',
};

export type BudgetTolerance = 'strict' | '5' | '10' | '20' | 'flexible';
export type LocationMode = 'none' | 'remote' | '50km' | '100km' | '200km';

/**
 * Poids interne de la localisation selon le mode.
 * 'none' (Sans contrainte) et 'remote' → 0 : le critère est totalement exclu
 * de la note finale (et sa barre est masquée dans les résultats).
 */
export function getLocationWeight(locationMode: LocationMode): number {
  switch (locationMode) {
    case 'remote': return 0;
    case '50km': return 4;
    case '100km': return 3;
    case '200km': return 2;
    case 'none': return 0;
    default: return 0;
  }
}

/**
 * Poids interne du budget selon la tolérance.
 * 'flexible' → 0 : le budget est totalement exclu de la note finale
 * (et sa barre est masquée dans les résultats).
 */
export function getBudgetWeight(tolerance: BudgetTolerance): number {
  switch (tolerance) {
    case 'strict': return 5;
    case '5': return 4;
    case '10': return 3;
    case '20': return 2;
    case 'flexible': return 0;
    default: return 3;
  }
}

/**
 * Calcule le scoreGlobal pour chaque résultat en fonction des poids.
 * Les 4 critères principaux ont des poids ajustables.
 * Localisation et budget ont des poids dérivés du mode/tolérance.
 */
export function rescore(
  results: MatchingResult[],
  weights: CriteriaWeights,
  locationMode: LocationMode = 'none',
  budgetTolerance: BudgetTolerance = '10',
): MatchingResultWithScore[] {
  const locWeight = getLocationWeight(locationMode);
  const budWeight = getBudgetWeight(budgetTolerance);

  const total =
    Object.values(weights).reduce((a, b) => a + b, 0) + locWeight + budWeight;

  return results
    .map((r) => ({
      ...r,
      scoreGlobal: total === 0 ? 0 : Math.round(
        (r.scoreDetail.poste * weights.poste +
         r.scoreDetail.competences * weights.competences +
         r.scoreDetail.experience * weights.experience +
         r.scoreDetail.secteurs * weights.secteurs +
         r.scoreDetail.localisation * locWeight +
         r.scoreDetail.budget * budWeight) / total,
      ),
    }))
    .sort((a, b) => {
      if (total === 0) return 0;
      return b.scoreGlobal - a.scoreGlobal;
    });
}
